import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Loader2, X } from "lucide-react";

/**
 * AddressAutocomplete — input with Google Places (New) autocomplete suggestions.
 *
 * Props :
 *   - value            : current text value
 *   - onChange(text)   : called on every keystroke
 *   - onSelect(place)  : called when user picks a suggestion. `place` is a normalized
 *                        object: { place_id, name, address, lat, lng, region, country, city, ... }
 *   - placeholder      : input placeholder
 *   - lang             : "fr" | "en" (default "fr")
 *   - region           : ISO 2-letter country bias (e.g. "fr", "it", "us"). Optional.
 *   - testId           : data-testid prefix
 *   - className        : extra classes on the wrapper
 *   - inputClassName   : classes applied to the <input>
 *   - autoFocus        : autofocus the input on mount
 */
export default function AddressAutocomplete({
  value, onChange, onSelect,
  placeholder = "Tapez une adresse, une ville…",
  lang = "fr", region,
  testId = "address-ac",
  className = "", inputClassName = "",
  autoFocus = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced fetch when value changes
  useEffect(() => {
    if (!value || value.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q: value.trim(), lang };
        if (region) params.region = region;
        const { data } = await api.get("/places/autocomplete", { params });
        setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
        setOpen(true);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [value, lang, region]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = async (s) => {
    setOpen(false);
    onChange?.(s.full);
    if (!onSelect) return;
    try {
      const { data } = await api.get(`/places/lookup/${s.place_id}`);
      onSelect({ ...data, suggestion: s });
    } catch {
      // Fall back to the lightweight suggestion if details lookup fails
      onSelect({ place_id: s.place_id, name: s.primary, address: s.full, suggestion: s });
    }
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pick(suggestions[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`} data-testid={testId}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E5276A] pointer-events-none" />
        <input
          type="text"
          autoFocus={autoFocus}
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
          className={`w-full pl-9 pr-9 py-2.5 rounded-lg border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none text-sm text-[#1F3A5F] bg-white ${inputClassName}`}
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B95A1] animate-spin" />
        )}
        {!loading && value && (
          <button
            type="button"
            onClick={() => { onChange?.(""); setSuggestions([]); setOpen(false); }}
            data-testid={`${testId}-clear`}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-[#8B95A1] hover:text-[#E5276A] hover:bg-[#FBF7F3]"
            aria-label="Effacer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testId}-list`}
          className="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-xl border border-[#1F3A5F]/15 bg-white shadow-lg"
        >
          {suggestions.map((s, i) => (
            <li key={s.place_id}>
              <button
                type="button"
                onClick={() => pick(s)}
                onMouseEnter={() => setActiveIdx(i)}
                data-testid={`${testId}-opt-${i}`}
                className={`w-full text-left px-3 py-2 flex items-start gap-2.5 ${activeIdx === i ? "bg-[#FCE4ED]" : "hover:bg-[#FBF7F3]"}`}
              >
                <MapPin className={`w-3.5 h-3.5 mt-1 shrink-0 ${activeIdx === i ? "text-[#E5276A]" : "text-[#8B95A1]"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#1F3A5F] truncate font-medium">{s.primary || s.full}</div>
                  {s.secondary && <div className="text-xs text-[#8B95A1] truncate">{s.secondary}</div>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
